/**
 * Amplifier profiles -- group 11 (European solid-state houses, one valve design,
 * one headphone amplifier).
 *
 * The interesting engineering variable across this group is not power but what
 * each design does with feedback. A switching output stage with 25 dB of loop
 * gain, a current-feedback bipolar stage that holds its loop gain flat to
 * hundreds of kilohertz, a low-feedback bipolar design optimised for transient
 * intermodulation, and a valve push-pull stage with an output transformer all
 * produce different residual spectra, different source impedances and therefore
 * different interaction with the loudspeaker impedance curve. The kit derives
 * those consequences from `topology` plus the published figures below.
 */

import type { AmpProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeAmp, usd } from '../kit';

export const AMPS_11: AmpProfile[] = [
  makeAmp({
    mfr: 'Pilium Audio',
    model: 'Alexander',
    price: usd(
      30000,
      'APPROXIMATE',
      2024,
      'Approximate US retail. Pilium sells through regional distribution and does not publish a global list price, so this is an estimate.',
    ),
    year: 2019,
    country: 'Greece',
    topology: 'solid-state-ab',
    form: 'integrated',
    devices: 'High-bias complementary bipolar output banks, dual-mono supplies with very large reservoir capacitance',
    power: { watts8: 200, watts4: 380, confidence: 'ESTIMATED' },
    gainDb: 38,
    thdPercent: 0.02,
    snrDb: 106,
    bandwidth: [2, 150000],
    dampingFactor: 400,
    feedbackDb: 24,
    crosstalkDb: -100,
    balancedInput: true,
    dynamics: { sagDb: 0.12, sagTimeMs: 30, recoveryMs: 80, thermalDb: 0.18, thermalTimeMs: 9000, slewRateVus: 80, headroomDb: 2.8 },
    clipping: { kind: 'asymmetric', kneeDb: 2.0, stickiness: 0.06 },
    confidence: 'ESTIMATED',
    accent: '#9b7bd4',
    tags: ['greece', 'high-bias', 'dual-mono', 'stiff-supply'],
    notes:
      'The defining parameter here is supply stiffness rather than topology: very large reservoir capacitance and dual-mono transformers give almost no rail sag on sustained peaks, which is modelled as 0.12 dB of dynamic compression against the 0.5 dB a conventional Class AB design shows. Standing bias is high enough that the crossover region is only reached at high output, so the class-B crossover notch in the nonlinear model is narrow and the residual spectrum stays low-order until the amplifier is worked hard. A damping factor of 400 means the loudspeaker impedance curve prints back onto the frequency response at well under 0.1 dB.',
    provenanceNote:
      'Topology class (high-bias complementary bipolar, dual-mono supply) follows Pilium published product description. Power into 8 and 4 ohms, THD, SNR, bandwidth, damping factor and feedback are ESTIMATED; Pilium does not publish a complete electrical specification for this model. Price is approximate.',
  }),

  makeAmp({
    mfr: 'Aavik',
    model: 'P-580',
    price: usd(
      36000,
      'APPROXIMATE',
      2023,
      'Approximate US retail for the stereo power amplifier; Aavik 580-series pricing varies by market.',
    ),
    year: 2019,
    country: 'Denmark',
    topology: 'class-d',
    form: 'stereo-power',
    devices: 'Proprietary UMAC switching output stage with active tesla-coil and dither-circuit noise reduction',
    power: { watts8: 300, watts4: 600, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 26,
    thdPercent: 0.008,
    snrDb: 112,
    bandwidth: [3, 60000],
    dampingFactor: 220,
    feedbackDb: 24,
    crosstalkDb: -108,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.classD,
    nonlinear: { kind: 'class-d-pwm', amount: 0.34, carrierHz: 400000, headroomDb: 8, asymmetry: 0.12, oversample: 4 },
    dynamics: { sagDb: 0.1, sagTimeMs: 20, recoveryMs: 50, slewRateVus: 22, headroomDb: 0.7 },
    clipping: { kind: 'hard', kneeDb: 0.35, stickiness: 0.04 },
    confidence: 'ESTIMATED',
    accent: '#3f9d8a',
    tags: ['denmark', 'switching', 'umac', 'output-filter'],
    notes:
      'A switching output stage whose audible fingerprint is set by its output filter rather than by its devices. The LC reconstruction filter sits above the audio band but its damping depends on the load, so unlike the linear amplifiers in this group the top-octave response is not load-independent, and the modelled bandwidth stops at 60 kHz instead of the 150-250 kHz the bipolar designs reach. Residual distortion is odd-order and largely level-independent, and clipping is abrupt with essentially no soft knee, so headroom above rated power is under 1 dB.',
    provenanceNote:
      'Rated power into 8 and 4 ohms and the use of a proprietary UMAC switching output stage follow Aavik published product documentation. THD, SNR, damping factor, bandwidth, switching carrier frequency and feedback depth are ESTIMATED; Aavik describes its noise-reduction circuitry qualitatively and does not publish a full distortion or bandwidth specification. Price is approximate.',
  }),

  makeAmp({
    mfr: 'Norma Audio',
    model: 'Revo SC-2',
    price: usd(
      5500,
      'APPROXIMATE',
      2023,
      'Approximate US retail for the line-stage configuration; the optional phono and DAC boards change the price.',
    ),
    year: 2013,
    country: 'Italy',
    topology: 'solid-state-a',
    form: 'preamp',
    devices: 'Discrete Class A line stage, very low global feedback, relay-switched resistor ladder volume control',
    power: { outputVrms: 12, confidence: 'ESTIMATED' },
    gainDb: 12,
    thdPercent: 0.006,
    snrDb: 108,
    bandwidth: [1, 500000],
    outputImpedanceOhms: 47,
    feedbackDb: 10,
    crosstalkDb: -102,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'jfet-square', amount: 0.42, asymmetry: 0.5, bias: 0.6, headroomDb: 16, oversample: 4 },
    dynamics: { sagDb: 0.05, sagTimeMs: 20, recoveryMs: 60, thermalDb: 0.25, thermalTimeMs: 9000, slewRateVus: 120, headroomDb: 12 },
    clipping: { kind: 'asymmetric', kneeDb: 3.5, stickiness: 0.05 },
    confidence: 'ESTIMATED',
    accent: '#8c5b6f',
    tags: ['italy', 'line-stage', 'wide-bandwidth', 'low-feedback', 'ladder-volume'],
    notes:
      'A line stage rather than a power amplifier, so the relevant model parameters are gain, headroom, output impedance and residual spectrum, not watts. Norma runs a very wide open-loop bandwidth with only about 10 dB of global feedback, which is modelled as a Class A second-harmonic-dominant residue that rises smoothly with level rather than the high-order tail a heavily corrected stage produces, and as a response that is still flat at 200 kHz. The 47 ohm output impedance and 12 dB of headroom above nominal mean it will drive a long interconnect into a 10 kohm power amplifier input without measurable top-octave loss.',
    provenanceNote:
      'Class A discrete line-stage topology, relay-switched resistor volume control and balanced inputs follow Norma Audio published product description. Gain, output level, THD, SNR, bandwidth, output impedance and feedback depth are ESTIMATED from that topology; Norma publishes bandwidth claims qualitatively rather than as a full specification table. Price is approximate and option-dependent.',
  }),

  makeAmp({
    mfr: 'Audia Flight',
    model: 'FL Three S',
    price: usd(
      4700,
      'APPROXIMATE',
      2023,
      'Approximate US retail; European list pricing differs and the model has been revised.',
    ),
    year: 2018,
    country: 'Italy',
    topology: 'solid-state-ab',
    form: 'integrated',
    devices: 'Discrete current-feedback voltage amplifier stage into complementary bipolar outputs',
    power: { watts8: 100, watts4: 180, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 36,
    thdPercent: 0.05,
    snrDb: 100,
    bandwidth: [1, 250000],
    dampingFactor: 250,
    feedbackDb: 20,
    crosstalkDb: -92,
    balancedInput: true,
    dynamics: { sagDb: 0.45, sagTimeMs: 55, recoveryMs: 130, thermalDb: 0.15, thermalTimeMs: 6000, slewRateVus: 200, headroomDb: 1.6 },
    clipping: { kind: 'asymmetric', kneeDb: 2.2, stickiness: 0.09 },
    confidence: 'ESTIMATED',
    accent: '#c46a4f',
    tags: ['italy', 'current-feedback', 'wide-bandwidth', 'high-slew'],
    notes:
      'Current-feedback amplifiers hold loop gain roughly constant with frequency instead of losing it at 6 dB per octave, so the distortion residue does not climb through the top two octaves the way a voltage-feedback design running out of correction does. That is modelled here as a 250 kHz small-signal bandwidth and a 200 V/us slew rate, which effectively removes slew-limiting as a source of transient intermodulation at any realistic level. Supply sag is more visible than in the Pilium entry because the reservoir bank is a fraction of the size, giving 0.45 dB of compression on sustained full-power passages.',
    provenanceNote:
      'Rated power into 8 and 4 ohms and the current-feedback topology follow Audia Flight published product documentation. THD, SNR, bandwidth, slew rate, damping factor and feedback depth are ESTIMATED from that topology and from the published power figures. Price is approximate.',
  }),

  makeAmp({
    mfr: 'Primare',
    model: 'I35 Prisma',
    price: usd(
      4500,
      'APPROXIMATE',
      2023,
      'Approximate US retail for the streaming/DAC-equipped Prisma configuration.',
    ),
    year: 2018,
    country: 'Sweden',
    topology: 'class-d',
    form: 'integrated',
    devices: 'UFPD 2 switching output stage with adaptive loop gain and a load-independent output filter',
    power: { watts8: 150, watts4: 300, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 34,
    thdPercent: 0.05,
    snrDb: 100,
    bandwidth: [8, 40000],
    dampingFactor: 300,
    feedbackDb: 30,
    crosstalkDb: -95,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.classD,
    nonlinear: { kind: 'class-d-pwm', amount: 0.45, carrierHz: 600000, headroomDb: 8, asymmetry: 0.08, oversample: 4 },
    dynamics: { sagDb: 0.2, sagTimeMs: 25, recoveryMs: 65, slewRateVus: 20, headroomDb: 0.9 },
    clipping: { kind: 'hard', kneeDb: 0.45, stickiness: 0.05 },
    confidence: 'ESTIMATED',
    accent: '#5b6f8c',
    tags: ['sweden', 'switching', 'ufpd', 'adaptive-loop-gain'],
    notes:
      'The design intent of UFPD is to keep the output filter response independent of load impedance, which is the opposite of the compromise the Aavik entry makes, so this profile is given a lower top-end corner but a much higher damping factor and much deeper feedback. The consequence in the model is a higher-order, lower-magnitude residual spectrum than a low-feedback linear amplifier produces: total distortion is modest at 0.05 percent but what remains is spread across odd orders rather than concentrated in the second. Carrier frequency is set higher than the Aavik entry, moving switching residue further above the audio band.',
    provenanceNote:
      'Rated power into 8 and 4 ohms, the UFPD 2 switching output stage and its stated load-independent filter behaviour follow Primare published product documentation. THD, SNR, damping factor, bandwidth, carrier frequency and feedback depth are ESTIMATED. Price is approximate and depends on the streaming module configuration.',
  }),

  makeAmp({
    mfr: 'Copland',
    model: 'CTA-408',
    price: usd(
      6000,
      'APPROXIMATE',
      2023,
      'Approximate US retail; valve integrated pricing varies with the supplied tube complement.',
    ),
    year: 2018,
    country: 'Denmark',
    topology: 'tube-push-pull',
    form: 'integrated',
    devices: '4 x KT88 in push-pull ultra-linear, 12AU7/12AX7 driver stage, moderate global feedback',
    power: { watts8: 60, watts4: 60, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 32,
    thdPercent: 0.5,
    snrDb: 90,
    bandwidth: [14, 45000],
    dampingFactor: 6,
    feedbackDb: 12,
    outputTransformer: true,
    transformerLfHz: 18,
    transformerHfHz: 40000,
    crosstalkDb: -66,
    harmonicShape: HARMONIC_SHAPES.pushPullTube,
    dynamics: { sagDb: 1.0, sagTimeMs: 170, recoveryMs: 400, slewRateVus: 16, headroomDb: 1.4 },
    clipping: { kind: 'soft', kneeDb: 6.5, stickiness: 0.32 },
    confidence: 'ESTIMATED',
    accent: '#d9a05b',
    tags: ['denmark', 'kt88', 'ultra-linear', 'output-transformer'],
    notes:
      'Push-pull operation cancels most of the second harmonic that a single-ended valve stage produces, so the residual spectrum is third-harmonic dominant with a slower decay than any of the solid-state entries here -- a genuinely different distortion structure, not just a different magnitude. The 1.33 ohm output impedance is the dominant audible variable: with a damping factor of 6 the loudspeaker impedance curve is printed onto the frequency response at up to about 1.5 dB on a typical 4-to-16 ohm swing, which no other amplifier in this group does. Output transformer core saturation sets the low-frequency limit and supply sag reaches 1 dB on sustained peaks.',
    provenanceNote:
      'Valve complement, ultra-linear push-pull topology, output transformer and rated power follow Copland published product documentation. THD, SNR, bandwidth, damping factor, feedback depth and transformer corner frequencies are ESTIMATED; Copland, like most valve manufacturers, does not publish a full distortion-versus-power specification. Price is approximate.',
  }),

  makeAmp({
    mfr: 'Electrocompaniet',
    model: 'Nemo AW600',
    price: usd(
      10000,
      'APPROXIMATE',
      2023,
      'Approximate US retail per monoblock, not per pair.',
    ),
    year: 2008,
    country: 'Norway',
    topology: 'solid-state-ab',
    form: 'monoblock',
    devices: 'Balanced complementary bipolar output banks, low global feedback in the Otala transient-intermodulation tradition',
    power: { watts8: 600, watts4: 1000, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 29,
    thdPercent: 0.005,
    snrDb: 110,
    bandwidth: [1, 200000],
    dampingFactor: 300,
    feedbackDb: 16,
    crosstalkDb: -110,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.classAB,
    nonlinear: { kind: 'class-b-crossover', amount: 0.38, crossoverWidth: 0.0025, headroomDb: 12, asymmetry: 0.12, oversample: 4 },
    dynamics: { sagDb: 0.25, sagTimeMs: 70, recoveryMs: 160, thermalDb: 0.1, thermalTimeMs: 7000, slewRateVus: 150, headroomDb: 2.2 },
    clipping: { kind: 'asymmetric', kneeDb: 3.0, stickiness: 0.07 },
    confidence: 'ESTIMATED',
    accent: '#4a7fb5',
    tags: ['norway', 'low-feedback', 'high-current', 'otala-lineage'],
    notes:
      'Electrocompaniet designs descend from Otala work on transient intermodulation, so the house rule is a wide open-loop bandwidth with modest global feedback rather than deep correction. Modelled at 16 dB of feedback, the crossover region is narrow but not fully erased, giving a low-magnitude residue that still contains identifiable higher odd orders when the amplifier is driven hard -- distinct from the Pilium entry, which uses more feedback and more bias, and from the switching entries, whose residue is level-independent. Doubling from 600 W into 8 ohms to 1000 W into 4 ohms indicates a current-limited rather than voltage-limited supply, which the model reflects as 0.25 dB of sag rather than none.',
    provenanceNote:
      'Rated power into 8 and 4 ohms, monoblock format and the low-feedback balanced topology follow Electrocompaniet published product documentation. THD, SNR, bandwidth, damping factor, feedback depth and slew rate are ESTIMATED from that topology and the published power figures. Price is approximate and is quoted per monoblock.',
  }),

  makeAmp({
    mfr: 'Simaudio Moon',
    model: '430HA',
    price: usd(
      4300,
      'APPROXIMATE',
      2023,
      'Approximate US retail for the version with the optional internal DAC omitted.',
    ),
    year: 2015,
    country: 'Canada',
    topology: 'solid-state-ab',
    form: 'headphone',
    devices: 'Fully balanced differential discrete output stage, four independent gain paths, resistor-ladder attenuator',
    power: { mw32: 5000, mw300: 1300, mw600: 700, confidence: 'ESTIMATED' },
    gainDb: 18,
    thdPercent: 0.001,
    snrDb: 118,
    bandwidth: [2, 200000],
    outputImpedanceOhms: 0.6,
    feedbackDb: 28,
    crosstalkDb: -105,
    balancedInput: true,
    dynamics: { sagDb: 0.05, sagTimeMs: 25, recoveryMs: 70, thermalDb: 0.08, thermalTimeMs: 5000, slewRateVus: 100, headroomDb: 3.5 },
    clipping: { kind: 'asymmetric', kneeDb: 2.0, stickiness: 0.05 },
    confidence: 'ESTIMATED',
    accent: '#6e9ec4',
    tags: ['canada', 'headphone', 'fully-balanced', 'low-output-impedance'],
    notes:
      'A headphone amplifier, so the model is driven by output power into 32, 300 and 600 ohms rather than by watts into a loudspeaker, and by a 0.6 ohm source impedance that keeps the damping factor above 50 even into a 32 ohm dynamic driver -- enough that headphone impedance peaks do not print onto the frequency response. Running fully balanced with four independent gain paths cancels even-order products and doubles available voltage swing into high-impedance loads, which is why the 600 ohm figure is unusually high relative to the 32 ohm figure. Substantial headroom above nominal listening level means the clipping model is essentially never reached in normal use.',
    provenanceNote:
      'Fully balanced differential topology, resistor-ladder attenuation and the existence of a high-power headphone output follow Simaudio published product documentation. The per-load output powers into 32, 300 and 600 ohms are ESTIMATED by scaling from the published output-power claim, and THD, SNR, bandwidth, output impedance and feedback depth are likewise estimates rather than quoted figures. Price is approximate and configuration-dependent.',
  }),
];
